/**
 * Swedish Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const sv: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Översikt',
  'nav.funds': 'Fonder',
  'nav.investments': 'Investeringar',
  'nav.investees': 'Investeringsmottagare',
  'nav.esg': 'ESG Granskning',
  'nav.compliance': 'Regelefterlevnad',
  'nav.reports': 'Rapporter',
  'nav.settings': 'Inställningar',
  'nav.users': 'Användare',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indikatorer',
  'nav.taxonomy': 'EU-taxonomi',
  'nav.filings': 'Inlämningar',
  'nav.calendar': 'Kalender',
  'nav.submit': 'Skicka data',
  'nav.submissions': 'Mina inlämningar',
  'nav.benchmarks': 'Referensvärden',
  'nav.messages': 'Meddelanden',
  'nav.portfolio': 'Portfölj',
  'nav.performance': 'Fondresultat',
  'nav.esgSummary': 'ESG Sammanfattning',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Effektmått',
  'nav.sdg': 'SDG-kartläggning',
  'nav.audit': 'Revisionsspår',
  'nav.snapshots': 'Dataögonblicksbilder',
  'nav.knowledgeGraph': 'Kunskapsgraf',
  'nav.geographic': 'Geografisk',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Assistent',
  'nav.tasks': 'Uppgifter',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatorisk monitor',

  // Common Actions
  'action.create': 'Skapa',
  'action.edit': 'Redigera',
  'action.delete': 'Radera',
  'action.save': 'Spara',
  'action.cancel': 'Avbryt',
  'action.submit': 'Skicka',
  'action.approve': 'Godkänn',
  'action.reject': 'Avvisa',
  'action.download': 'Ladda ner',
  'action.export': 'Exportera',
  'action.import': 'Importera',
  'action.filter': 'Filtrera',
  'action.search': 'Sök',
  'action.refresh': 'Uppdatera',
  'action.close': 'Stäng',
  'action.back': 'Tillbaka',
  'action.next': 'Nästa',
  'action.previous': 'Föregående',
  'action.viewAll': 'Visa alla',
  'action.viewDetails': 'Visa detaljer',
  'action.generate': 'Generera',
  'action.signOut': 'Logga ut',
  'action.addNew': 'Lägg till ny',
  'action.invite': 'Bjud in',

  // Statuses
  'status.draft': 'Utkast',
  'status.submitted': 'Inskickat',
  'status.validated': 'Validerat',
  'status.approved': 'Godkänt',
  'status.rejected': 'Avvisat',
  'status.pending': 'Väntande',
  'status.inProgress': 'Pågående',
  'status.overdue': 'Försenat',
  'status.compliant': 'Uppfyllt',
  'status.filed': 'Inlämnat',
  'status.active': 'Aktiv',
  'status.inactive': 'Inaktiv',
  'status.closed': 'Stängd',
  'status.fundraising': 'Kapitalanskaffning',
  'status.investing': 'Investering',
  'status.harvesting': 'Skörd',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portföljöversikt',
  'dashboard.complianceStatus': 'Efterlevnadsstatus',
  'dashboard.collectionProgress': 'Insamlingsframsteg',
  'dashboard.activityFeed': 'Aktivitet',
  'dashboard.quickActions': 'Snabbåtgärder',
  'dashboard.investmentPipeline': 'Investeringspipeline',
  'dashboard.totalFunds': 'Totalt fonder',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktiva investeringar',
  'dashboard.totalInvestees': 'Investeringsmottagare',
  'dashboard.recentActivity': 'Senaste aktivitet',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Totalt åtagande',
  'lp.capitalCalled': 'Inropat kapital',
  'lp.distributed': 'Distribuerat',
  'lp.netPosition': 'Nettoposition',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fondresultat',
  'lp.esgSummary': 'ESG Sammanfattning',
  'lp.reportLibrary': 'Rapportbibliotek',
  'lp.taxonomyAligned': 'Taxonomianpassad',
  'lp.paiCoverage': 'PAI Täckning',
  'lp.portfolioPerformance': 'LP Portföljresultat',

  // Fund Labels
  'fund.name': 'Fondnamn',
  'fund.code': 'Fondkod',
  'fund.vintage': 'Grundläggningsår',
  'fund.size': 'Fondstorlek',
  'fund.targetSize': 'Målstorlek',
  'fund.currency': 'Valuta',
  'fund.strategy': 'Strategi',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Kategori',
  'fund.geographyFocus': 'Geografiskt fokus',
  'fund.sectorFocus': 'Sektorfokus',

  // Form Labels
  'form.name': 'Namn',
  'form.email': 'E-post',
  'form.country': 'Land',
  'form.amount': 'Belopp',
  'form.currency': 'Valuta',
  'form.date': 'Datum',
  'form.description': 'Beskrivning',
  'form.notes': 'Anteckningar',
  'form.type': 'Typ',
  'form.period': 'Period',
  'form.year': 'År',
  'form.required': 'Obligatorisk',
  'form.optional': 'Valfritt',

  // Table Headers
  'table.fund': 'Fond',
  'table.commitment': 'Åtagande',
  'table.called': 'Inropat',
  'table.distributed': 'Distribuerat',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Reglering',
  'table.filingType': 'Inlämningstyp',
  'table.reportingPeriod': 'Period',
  'table.createdAt': 'Skapad',
  'table.actions': 'Åtgärder',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU-taxonomi',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Datakompletthet',
  'compliance.nextDeadline': 'Nästa deadline',

  // Reports
  'report.quarterly': 'Kvartalsrapport',
  'report.annual': 'Årsrapport',
  'report.paiStatement': 'PAI Uttalande',
  'report.taxonomyReport': 'Taxonomirapport',
  'report.generatedAt': 'Genererad',
  'report.version': 'Version',

  // General
  'general.loading': 'Laddar...',
  'general.noData': 'Ingen data tillgänglig',
  'general.error': 'Ett fel uppstod',
  'general.retry': 'Försök igen',
  'general.total': 'Totalt',
  'general.page': 'Sida',
  'general.of': 'av',
  'general.showing': 'Visar',
  'general.results': 'resultat',
  'general.language': 'Språk',
  'general.english': 'Engelska',
  'general.french': 'Franska',

  // Tour
  'tour.takeTour': 'Starta rundtur',
  'tour.skip': 'Hoppa over',
  'tour.next': 'Nasta',
  'tour.prev': 'Foregaende',
  'tour.finish': 'Slutfor',
};
